import AppBackButton from '@nxsys/components/AppBackButton';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppSectionNav, {
  SectionNavGroup,
  SectionNavItem,
} from '@nxsys/components/AppSectionNav';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { useGetAgencyById } from '@nxsys/hooks/api/agency';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { formatStatus } from '@nxsys/utils';
import { Space, Typography } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AgencyContact from '../listing/AgencyContact';
import Branches from '../listing/Branches';
import DocumentAgency from '../listing/DocumentAgency';
import TimesheetAgency from '../listing/TimesheetAgency';
import AgencyAssignmentTab from './AgencyAssignmentTab';
import ContactDetail from './ContactDetail';
import EditPayrollFinance from './EditPayrollFinance';
import GeneralInformation from './GeneralInformation';
import styled from 'styled-components';

enum AgencyTabsKey {
  GENERAL_INFORMATION = 'GENERAL_INFORMATION',
  CONTACT_DETAIL = 'CONTACT_DETAIL',
  PAYROLL_AND_FINANCE = 'PAYROLL_AND_FINANCE',
  DOCUMENT = 'DOCUMENT',
  ASSIGNMENT = 'ASSIGNMENT',
  TIMESHEET_ALL = 'TIMESHEET_ALL',
  TIMESHEET_PENDING_APPROVAL = 'TIMESHEET_PENDING_APPROVAL',
  BRANCHES = 'BRANCHES',
  AGENCY_CONTACT = 'AGENCY_CONTACT',
}

const TIMESHEET_GROUP_KEY = 'TIMESHEET';

const TAB_TITLE_KEY: Record<AgencyTabsKey, string> = {
  [AgencyTabsKey.GENERAL_INFORMATION]: 'common.generalInformation',
  [AgencyTabsKey.CONTACT_DETAIL]: 'common.contactDetail',
  [AgencyTabsKey.PAYROLL_AND_FINANCE]: 'agency.detail.payrollAndFinance',
  [AgencyTabsKey.DOCUMENT]: 'common.document',
  [AgencyTabsKey.ASSIGNMENT]: 'common.assignment',
  [AgencyTabsKey.TIMESHEET_ALL]: 'common.timesheet',
  [AgencyTabsKey.TIMESHEET_PENDING_APPROVAL]: 'common.timesheet',
  [AgencyTabsKey.BRANCHES]: 'common.branches',
  [AgencyTabsKey.AGENCY_CONTACT]: 'common.agencyContacts',
};

const FormWrapper = styled.div`
  .ant-form-item .ant-form-item-label {
    display: flex;
    align-items: center;
  }
  .ant-form-item .ant-form-item-label > label {
    min-height: unset;
  }

  .ant-form-item-control-input-content:has(.ant-switch) {
    display: flex;
    justify-content: flex-end;
  }
`;

const AgencyDetail = () => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const agencyId = params?.agencyId ?? null;
  const navigate = useNavigateWithQuery();
  const location = useLocation();

  const {
    run: fetchAgency,
    loading: isFetchingAgency,
    value: agencyValue,
  } = useGetAgencyById();
  const agencyData = useMemo(() => agencyValue?.data, [agencyValue]);

  useEffect(() => {
    if (agencyId) fetchAgency(agencyId);
  }, [agencyId]);

  const [activeTab, setActiveTab] = useState<AgencyTabsKey>(
    AgencyTabsKey.GENERAL_INFORMATION,
  );

  const isValidTabKey = useCallback((key: string): key is AgencyTabsKey => {
    return (Object.values(AgencyTabsKey) as string[]).includes(key);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && isValidTabKey(tab)) {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab(AgencyTabsKey.GENERAL_INFORMATION);
    }
  }, [isValidTabKey, location.search]);

  const renderTabContent = useCallback(
    (key: AgencyTabsKey) => {
      if (!agencyData) return <></>;
      const titleKey = TAB_TITLE_KEY[key];
      const titleBlock = titleKey ? (
        <div className="mb-6 mt-4">
          <Typography.Text className="font-medium text-gray-800 text-2xl">
            {messages[titleKey]}
          </Typography.Text>
        </div>
      ) : null;
      const formTabWrapper = (content: React.ReactNode) => (
        <div className="px-6 py-6 bg-white rounded-lg flex justify-center">
          <div className="max-w-[600px] w-full">
            {titleBlock}
            {content}
          </div>
        </div>
      );
      const fullWidthWrapper = (content: React.ReactNode) => (
        <div className="px-6 py-6 bg-white rounded-lg">
          {titleBlock}
          {content}
        </div>
      );
      switch (key) {
        case AgencyTabsKey.GENERAL_INFORMATION:
          return formTabWrapper(<GeneralInformation data={agencyData} />);
        case AgencyTabsKey.CONTACT_DETAIL:
          return formTabWrapper(<ContactDetail data={agencyData} />);
        case AgencyTabsKey.PAYROLL_AND_FINANCE:
          return formTabWrapper(<EditPayrollFinance />);
        case AgencyTabsKey.ASSIGNMENT:
          return <AgencyAssignmentTab agency={agencyData} />;
        case AgencyTabsKey.DOCUMENT:
          return fullWidthWrapper(
            <DocumentAgency data={agencyData} hasBorder={false} />,
          );
        case AgencyTabsKey.TIMESHEET_ALL:
          return fullWidthWrapper(
            <TimesheetAgency
              data={agencyData}
              hasBorder={false}
              status="ALL"
            />,
          );
        case AgencyTabsKey.TIMESHEET_PENDING_APPROVAL:
          return fullWidthWrapper(
            <TimesheetAgency
              data={agencyData}
              hasBorder={false}
              approveStatus="PENDING"
            />,
          );
        case AgencyTabsKey.BRANCHES:
          return fullWidthWrapper(
            <Branches data={agencyData} hasBorder={false} />,
          );
        case AgencyTabsKey.AGENCY_CONTACT:
          return fullWidthWrapper(
            <AgencyContact data={agencyData} hasBorder={false} />,
          );
        default:
          return <></>;
      }
    },
    [agencyData, messages],
  );

  const navItems = useMemo(() => {
    const goToTab = (key: AgencyTabsKey) => {
      navigate(`/agency/${agencyId}`, {
        query: {
          tab: key,
        },
      });
    };

    const leaf = (item: {
      key: AgencyTabsKey;
      label: SectionNavItem['label'];
    }): SectionNavItem => ({
      key: item.key,
      label: item.label,
      onClick: () => goToTab(item.key),
    });

    const timesheetGroup: SectionNavGroup = {
      key: TIMESHEET_GROUP_KEY,
      label: messages['common.timesheet'],
      children: [
        {
          key: AgencyTabsKey.TIMESHEET_ALL,
          label: messages['common.all'] ?? 'All',
          onClick: () => goToTab(AgencyTabsKey.TIMESHEET_ALL),
        },
        {
          key: AgencyTabsKey.TIMESHEET_PENDING_APPROVAL,
          label:
            messages['common.timesheetPendingApproval'] ?? 'Pending Approval',
          onClick: () => goToTab(AgencyTabsKey.TIMESHEET_PENDING_APPROVAL),
        },
      ],
    };

    return [
      leaf({
        key: AgencyTabsKey.GENERAL_INFORMATION,
        label: messages['common.generalInformation'],
      }),
      leaf({
        key: AgencyTabsKey.CONTACT_DETAIL,
        label: messages['common.contactDetail'],
      }),
      leaf({
        key: AgencyTabsKey.PAYROLL_AND_FINANCE,
        label: messages['agency.detail.payrollAndFinance'],
      }),
      leaf({
        key: AgencyTabsKey.DOCUMENT,
        label: messages['common.document'],
      }),
      leaf({
        key: AgencyTabsKey.ASSIGNMENT,
        label: messages['common.assignment'],
      }),
      timesheetGroup,
      leaf({
        key: AgencyTabsKey.BRANCHES,
        label: messages['common.branches'],
      }),
      leaf({
        key: AgencyTabsKey.AGENCY_CONTACT,
        label: messages['common.agencyContacts'],
      }),
    ] as Array<SectionNavItem | SectionNavGroup>;
  }, [agencyId, messages, navigate]);

  const renderTitle = useCallback(
    () => (
      <Space>
        <AppBackButton
          onClick={() => navigate('/agency', { excludeParams: ['tab'] })}
        />
        <Space direction="vertical" size={6}>
          <div className="flex items-center gap-x-2">
            <Typography.Title level={4} className="mb-0">
              {agencyData?.name ?? ''}
            </Typography.Title>
            <AppTagStatus className={formatStatus(agencyData?.status ?? '')}>
              {formatStatus(agencyData?.status ?? '')}
            </AppTagStatus>
          </div>
          <div className="flex items-center gap-x-2">
            <Typography.Text className="font-sm text-gray-500 border-solid border-gray-200 border-r pr-2">{`ID: ${agencyData?.id ?? ''}`}</Typography.Text>
            <Typography.Text className="font-sm text-gray-500">{`${messages['common.updatedOn']}: ${moment(agencyData?.updatedAt).format('DD/MM/YYYY')}`}</Typography.Text>
          </div>
        </Space>
      </Space>
    ),
    [agencyData],
  );

  return (
    <div className="AgencyDetail">
      <FormWrapper>
        <AppsContainer
          fullView
          type="bottom"
          title={renderTitle()}
          loading={isFetchingAgency}
        >
          <div className="flex gap-6 max-lg:flex-col">
            <div className="w-[296px] shrink-0 max-lg:w-full">
              <AppSectionNav items={navItems} activeKey={activeTab} />
            </div>
            <div className="flex-1 min-w-0">{renderTabContent(activeTab)}</div>
          </div>
        </AppsContainer>
      </FormWrapper>
    </div>
  );
};

export default AgencyDetail;
